import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { FileText, MapPin, Users, Vote, CheckCircle, ArrowRight, Building2, Shield, TrendingUp, Search, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { IndicativeDisclaimer } from "@/components/IndicativeDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LandingPage = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const handleStart = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      login();
    }
  };

  const [zoneQuery, setZoneQuery] = useState({ lat: "", lng: "" });
  const [zoneResult, setZoneResult] = useState(null);
  const [zoneLoading, setZoneLoading] = useState(false);

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setZoneQuery({ lat: pos.coords.latitude.toFixed(4), lng: pos.coords.longitude.toFixed(4) }),
      () => {}
    );
  };

  const checkZone = async () => {
    if (!zoneQuery.lat || !zoneQuery.lng) return;
    setZoneLoading(true);
    setZoneResult(null);
    try {
      const res = await fetch(`${API}/public/zone-check?lat=${zoneQuery.lat}&lng=${zoneQuery.lng}`);
      if (res.ok) setZoneResult(await res.json());
    } catch {
      // ignore
    } finally {
      setZoneLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: "Document Vault",
      description: "Securely upload and manage all redevelopment documents with AI-powered verification"
    },
    {
      icon: TrendingUp,
      title: "Readiness Score",
      description: "Track your society's redevelopment readiness with real-time progress indicators"
    },
    {
      icon: MapPin,
      title: "Location Analysis",
      description: "Get FSI entitlements and market insights based on your exact location"
    },
    {
      icon: Vote,
      title: "Member Voting",
      description: "Transparent developer selection with simple and weighted voting options"
    },
    {
      icon: CheckCircle,
      title: "79A Process Tracker",
      description: "Step-by-step tracking of the complete 79A redevelopment process"
    },
    {
      icon: Shield,
      title: "AI Feasibility",
      description: "Comprehensive AI-generated feasibility reports for informed decisions"
    }
  ];

  const steps = [
    { number: "01", title: "Register Your Society", description: "Create an account and add your society details" },
    { number: "02", title: "Upload Documents", description: "Upload required documents for readiness assessment" },
    { number: "03", title: "Get AI Analysis", description: "Receive comprehensive feasibility reports" },
    { number: "04", title: "Track Progress", description: "Monitor 79A process and conduct member voting" }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-8 h-8 text-emerald-700" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              ReDevelop
            </span>
          </div>
          <GoogleSignInButton label="Sign in with Google" />
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient relative pt-20 flex flex-col min-h-[90vh]">
        <div className="flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-6 py-16 w-full relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-emerald-900/50 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
                <Shield className="w-4 h-4" />
                DCPR 2034 &amp; UDCPR 2020 · All of Maharashtra
              </div>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in stagger-1"
                style={{ fontFamily: 'Manrope' }}
              >
                Make Your Society
                <span className="text-emerald-400 block">Redevelopment Ready</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-2xl animate-fade-in stagger-2 leading-relaxed">
                AI-powered platform to streamline your housing society redevelopment journey. 
                From document management to developer selection - all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
                <Button 
                  onClick={handleStart}
                  data-testid="hero-cta-btn"
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-base sm:text-lg"
                >
                  Start Free Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-slate-500 text-white hover:bg-slate-800 px-8 py-6 text-base sm:text-lg"
                  onClick={() => {
                    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-testid="watch-demo-btn"
                >
                  See How It Works
                </Button>
              </div>
              <p className="text-sm text-slate-400 mt-4 animate-fade-in stagger-3">
                Free to start • Sign in securely with your Google account
              </p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="bg-slate-900/80 backdrop-blur-md border-t border-slate-700">
          <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "DCPR 2034", label: "Mumbai regulation" },
              { value: "UDCPR 2020", label: "Rest of Maharashtra" },
              { value: "Zone-aware", label: "FSI & DP checks" },
              { value: "Secure Vault", label: "Encrypted documents" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Manrope' }}>
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-slate-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zone Check Widget */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              Check Your Society's Zone — Free
            </h2>
            <p className="text-slate-400">
              Enter your location to instantly see your zone, governing regulation (DCPR 2034 / UDCPR 2020) and indicative base FSI
            </p>
          </div>
          <div className="bg-slate-800/70 border border-slate-700 rounded-2xl p-6" data-testid="zone-check-widget">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Latitude (e.g. 19.2183)"
                value={zoneQuery.lat}
                onChange={(e) => setZoneQuery({ ...zoneQuery, lat: e.target.value })}
                className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                data-testid="zone-lat-input"
              />
              <input
                type="text"
                placeholder="Longitude (e.g. 72.9781)"
                value={zoneQuery.lng}
                onChange={(e) => setZoneQuery({ ...zoneQuery, lng: e.target.value })}
                className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                data-testid="zone-lng-input"
              />
              <Button
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700 py-6"
                onClick={detectLocation}
                data-testid="zone-detect-btn"
              >
                <MapPin className="w-4 h-4" />
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white py-6 px-6"
                onClick={checkZone}
                disabled={zoneLoading}
                data-testid="zone-check-btn"
              >
                {zoneLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Check Zone
              </Button>
            </div>
            {zoneResult && (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="zone-result">
                <div className="bg-slate-900 rounded-lg p-4 border border-emerald-800">
                  <p className="text-xs text-slate-400 mb-1">Zone</p>
                  <p className="text-emerald-400 font-semibold">{zoneResult.zone}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 border border-purple-800">
                  <p className="text-xs text-slate-400 mb-1">Governing Regulation</p>
                  <p className="text-purple-400 font-semibold">{zoneResult.regulation}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 border border-blue-800">
                  <p className="text-xs text-slate-400 mb-1">Indicative Base FSI</p>
                  <p className="text-blue-400 font-semibold">{zoneResult.base_fsi}</p>
                </div>
                <div className="sm:col-span-3 text-center mt-2">
                  <Button onClick={handleStart} className="bg-emerald-600 hover:bg-emerald-700" data-testid="zone-cta-btn">
                    Get Your Free Pre-feasibility Report
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="sm:col-span-3">
                  <IndicativeDisclaimer />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Everything You Need for Redevelopment
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive tools for housing societies across Mumbai, Navi Mumbai, Thane, Pune & all of Maharashtra
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card key={idx} className="feature-card group animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                <CardContent className="p-0">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-700 transition-colors">
                    <feature.icon className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              How It Works
            </h2>
            <p className="text-lg text-slate-600">Simple steps to get your society redevelopment ready</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative animate-fade-in" style={{ animationDelay: `${idx * 0.15}s` }}>
                <div className="text-6xl font-bold text-emerald-100 mb-4" style={{ fontFamily: 'Manrope' }}>
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                  {step.title}
                </h3>
                <p className="text-slate-600">{step.description}</p>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-1/2 border-t-2 border-dashed border-emerald-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Ready to Start Your Redevelopment Journey?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Built for Maharashtra housing societies preparing for redevelopment.
          </p>
          <div className="flex justify-center">
            <GoogleSignInButton label="Sign in with Google to Get Started" className="px-8 py-3 text-base" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-emerald-500" />
              <span className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>ReDevelop</span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 ReDevelop. Empowering Maharashtra Housing Societies.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
